import { useEffect, useRef, useState } from 'react';
import type { MermaidConfig } from 'mermaid';
import { useTheme } from '@mui/material/styles';
import { Button, IconButton, Tooltip } from '@mui/material';
import { Copy as CopyIcon, Check as CheckIcon, Expand as ExpandIcon } from 'lucide-react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { prism, vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import MermaidFullscreenViewer from './MermaidFullscreenViewer';
import styles from './MermaidDiagram.module.scss';

interface MermaidDiagramProps {
  code: string;
  isStreaming?: boolean;
}

let diagramCounter = 0;

interface RenderResult {
  svg: string | null;
  error: string | null;
  isUnsupportedType?: boolean;
}

const SUPPORTED_TYPES = ['flowchart', 'sequenceDiagram', 'pie', 'erDiagram', 'gantt'] as const;
type SupportedType = (typeof SUPPORTED_TYPES)[number];

function preprocessMermaid(raw: string): string {
  return raw
    .replace(/\r\n/g, '\n')
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/—/g, '-')
    .replace(/→/g, '-->')
    .replace(/←/g, '<--')
    .replace(/\\n/g, '\n')
    .trim();
}

function normalizeDiagramType(raw: string): string {
  if (/^\s*graph\b/im.test(raw)) return raw.replace(/^\s*graph\b/im, 'flowchart');
  return raw;
}

function detectDiagramType(code: string): SupportedType | null {
  const firstLine = code.split('\n').find((line) => line.trim());
  if (!firstLine) return null;
  const trimmed = firstLine.trim();
  if (/^flowchart\b/i.test(trimmed)) return 'flowchart';
  if (/^sequenceDiagram\b/i.test(trimmed)) return 'sequenceDiagram';
  if (/^pie\b/i.test(trimmed)) return 'pie';
  if (/^erDiagram\b/i.test(trimmed)) return 'erDiagram';
  if (/^gantt\b/i.test(trimmed)) return 'gantt';
  return null;
}

function validateMermaidStructure(code: string): string | null {
  const firstLine = code.split('\n').find((line) => line.trim());
  if (!firstLine) return 'Пустой Mermaid блок';
  if (!detectDiagramType(code)) {
    const keyword = firstLine.trim().split(/\s+/)[0];
    return `Тип диаграммы "${keyword}" не поддерживается. Поддерживаются: ${SUPPORTED_TYPES.join(', ')}`;
  }
  const body = code
    .split('\n')
    .slice(1)
    .some((line) => line.trim() && !line.trim().startsWith('%%'));
  if (!body) return 'В диаграмме нет инструкций после заголовка';
  return null;
}

function autoRepairMermaid(code: string): string {
  const withNormalizedType = normalizeDiagramType(code);
  const type = detectDiagramType(withNormalizedType);
  const lines = withNormalizedType.split('\n');
  const repairedLines = lines
    .map((rawLine) => {
      let line = rawLine.replace(/\r/g, '');
      const trimmed = line.trim();

      // Декоративные конструкции часто ломают LLM-генерацию в flowchart
      if (/^(classDef|class|style|linkStyle|click)\b/i.test(trimmed)) return '';

      // Убираем class-украшения вида :::root
      line = line.replace(/:::[A-Za-z0-9_-]+/g, '');

      // Нормализуем "сломанные" стрелки после генерации/починки
      line = line.replace(/\s-\s-\s-/g, ' --> ');
      line = line.replace(/-\s*->/g, '-->');
      line = line.replace(/<\s*--/g, '<--');

      // Для flowchart/sequenceDiagram часто помогает убрать двойные кавычки в метках
      if (type === 'flowchart' || type === 'sequenceDiagram') {
        line = line.replace(/"/g, "'");
      }

      return line;
    })
    .filter((line) => line.trim().length > 0);

  const lightlyRepaired = repairedLines
    .join('\n')
    .replace(/[`$]/g, '')
    .replace(/[^\t\n\r\x20-\x7E\u0400-\u04FF]/g, '');

  if (type === 'pie' || type === 'erDiagram' || type === 'gantt') {
    return lightlyRepaired;
  }

  if (/^\s*sequenceDiagram\b/im.test(lightlyRepaired)) {
    return lightlyRepaired.replace(/[()]/g, '');
  }
  if (/^\s*flowchart\b/im.test(lightlyRepaired)) {
    return lightlyRepaired.replace(/[()]/g, '').replace(/:/g, ' -');
  }
  return `flowchart TD\n  N1["Старт"] --> N2["Финиш"]`;
}

function logRenderMetric(key: 'success' | 'error') {
  try {
    const metricKey = 'mermaid_render_metrics_v1';
    const parsed = JSON.parse(localStorage.getItem(metricKey) || '{}') as {
      success?: number;
      error?: number;
    };
    const next = { success: parsed.success ?? 0, error: parsed.error ?? 0 };
    next[key] += 1;
    localStorage.setItem(metricKey, JSON.stringify(next));
  } catch {
    // ignore metrics failures
  }
}

const MermaidDiagram = ({ code, isStreaming }: MermaidDiagramProps) => {
  const [svg, setSvg] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [isUnsupportedTypeError, setIsUnsupportedTypeError] = useState(false);
  const [displayedCode, setDisplayedCode] = useState(code);
  const [manualRetryTick, setManualRetryTick] = useState(0);
  const [copied, setCopied] = useState(false);
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const codeStyle = isDark ? vscDarkPlus : prism;
  const idRef = useRef(`mermaid-diagram-${++diagramCounter}`);

  useEffect(() => {
    if (isStreaming || !code.trim()) {
      setSvg(null);
      setHasError(false);
      setErrorText(null);
      setDisplayedCode(code);
      return;
    }

    let cancelled = false;

    const config: MermaidConfig = {
      startOnLoad: false,
      theme: isDark ? 'dark' : 'default',
      // antiscript разрешает <br/> и HTML в метках, но блокирует <script>
      securityLevel: 'antiscript',
      fontFamily: 'inherit',
    };

    const renderOnce = async (source: string): Promise<RenderResult> => {
      const prepared = normalizeDiagramType(preprocessMermaid(source));
      const structureError = validateMermaidStructure(prepared);
      if (structureError) {
        return {
          svg: null,
          error: structureError,
          isUnsupportedType: !detectDiagramType(prepared),
        };
      }
      const renderId = `${idRef.current}-${Date.now()}`;
      try {
        const { default: mermaid } = await import('mermaid');
        if (cancelled) return { svg: null, error: 'render cancelled' };
        mermaid.initialize(config);
        const result = await mermaid.render(renderId, prepared);
        if (result.svg.includes('class="error-icon"') || result.svg.includes('Syntax error')) {
          document.getElementById('d' + renderId)?.remove();
          return { svg: null, error: 'Mermaid вернул error-SVG' };
        }
        return { svg: result.svg, error: null };
      } catch (error) {
        // Mermaid создаёт контейнер "d{id}" в document.body до рендера;
        // при ошибке он не удаляется сам — чистим вручную.
        document.getElementById('d' + renderId)?.remove();
        return {
          svg: null,
          error: error instanceof Error ? error.message : 'Ошибка рендера Mermaid',
        };
      }
    };

    const run = async () => {
      const first = await renderOnce(code);
      if (cancelled) return;
      if (first.svg) {
        setDisplayedCode(code);
        setSvg(first.svg);
        setHasError(false);
        setErrorText(null);
        logRenderMetric('success');
        return;
      }

      if (first.isUnsupportedType) {
        setDisplayedCode(code);
        setSvg(null);
        setHasError(true);
        setIsUnsupportedTypeError(true);
        setErrorText(first.error);
        logRenderMetric('error');
        return;
      }
      setIsUnsupportedTypeError(false);

      const repairedCode = autoRepairMermaid(code);
      const second = await renderOnce(repairedCode);
      if (cancelled) return;
      if (second.svg) {
        setDisplayedCode(repairedCode);
        setSvg(second.svg);
        setHasError(false);
        setErrorText(null);
        logRenderMetric('success');
        return;
      }

      setDisplayedCode(repairedCode);
      setSvg(null);
      setHasError(true);
      setErrorText(second.error ?? first.error ?? 'Не удалось отрендерить Mermaid');
      logRenderMetric('error');
      console.warn('Mermaid render failed:', {
        firstError: first.error,
        secondError: second.error,
        snippet: repairedCode.split('\n').slice(0, 3).join(' | '),
      });
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [code, isStreaming, isDark, manualRetryTick]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  // Во время стриминга показываем код, как обычный блок
  if (isStreaming) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.header}>
          <span>mermaid</span>
        </div>
        <SyntaxHighlighter
          style={codeStyle}
          language="markdown"
          PreTag="div"
          customStyle={{ background: 'transparent', padding: '12px 16px', margin: 0 }}
        >
          {displayedCode}
        </SyntaxHighlighter>
      </div>
    );
  }

  // Ошибка парсинга — показываем исходный код
  if (hasError) {
    if (isUnsupportedTypeError) {
      return (
        <div className={styles.wrapper}>
          <div className={styles.header}>
            <span>mermaid</span>
          </div>
          {errorText && <div className={styles.errorText}>{errorText}</div>}
        </div>
      );
    }
    return (
      <div className={styles.wrapper}>
        <div className={styles.header}>
          <span>mermaid</span>
          <Tooltip title="Скопировать код">
            <IconButton size="small" onClick={handleCopy} className={styles.copyBtn}>
              {copied ? <CheckIcon size={12} /> : <CopyIcon size={12} />}
            </IconButton>
          </Tooltip>
        </div>
        {errorText && <div className={styles.errorText}>{errorText}</div>}
        <div className={styles.errorActions}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setManualRetryTick((prev) => prev + 1)}
            className={styles.retryBtn}
          >
            Исправить диаграмму
          </Button>
        </div>
        <SyntaxHighlighter
          style={codeStyle}
          language="markdown"
          PreTag="div"
          customStyle={{ background: 'transparent', padding: '12px 16px', margin: 0 }}
        >
          {displayedCode}
        </SyntaxHighlighter>
      </div>
    );
  }

  // Диаграмма ещё рендерится
  if (!svg) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.header}>
          <span>mermaid</span>
        </div>
        <div className={styles.loading}>
          <span />
          <span />
          <span />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <span>mermaid</span>
        <div className={styles.headerActions}>
          <Tooltip title="Открыть в полном экране">
            <IconButton
              size="small"
              onClick={() => setIsFullscreenOpen(true)}
              className={styles.copyBtn}
              aria-label="Открыть диаграмму в полном экране"
            >
              <ExpandIcon size={12} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Скопировать код">
            <IconButton size="small" onClick={handleCopy} className={styles.copyBtn}>
              {copied ? <CheckIcon size={12} /> : <CopyIcon size={12} />}
            </IconButton>
          </Tooltip>
        </div>
      </div>
      <div className={styles.diagram} dangerouslySetInnerHTML={{ __html: svg }} />
      <MermaidFullscreenViewer
        open={isFullscreenOpen}
        svg={svg}
        onClose={() => setIsFullscreenOpen(false)}
      />
    </div>
  );
};

export default MermaidDiagram;
